import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getWorkspace } from "../lib/workspaceStore";
import { api } from "../lib/api";
import WebsiteCta from "../components/WebsiteCta";
import { websiteCtaUrl } from "../lib/pageSafety";
import { getProductHandoff } from "../lib/productHandoff";

function SectionRenderer({ schema }) {
  if (!schema?.sections) return null;
  const accent = schema?.theme?.accent || "#6C4AF2";
  const ctaHref = schema?.cta_url;

  const buttonStyle = {
    background: accent,
    color: "#000",
    borderRadius: 12,
    padding: "12px 16px",
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
  };

  return (
    <div style={{ background: "#000", color: "#fff", minHeight: "100vh" }}>
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "28px 16px" }}>
        {schema.sections.map((s, idx) => {
          if (s.type === "hero") {
            return (
              <div key={idx} style={{ padding: "36px 0" }}>
                <div style={{ display: "inline-flex", gap: 8, alignItems: "center", border: "1px solid rgba(255,255,255,0.12)", padding: "6px 10px", borderRadius: 999, background: "rgba(255,255,255,0.05)", fontSize: 12 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: accent }} />
                  Built by OmniMark HQ Website AI
                </div>
                <h1 style={{ marginTop: 18, fontSize: 44, lineHeight: 1.08, letterSpacing: -0.5 }}>
                  {s.props?.headline || "Your headline here"}
                </h1>
                <p style={{ marginTop: 12, fontSize: 16, lineHeight: 1.6, color: "rgba(255,255,255,0.72)" }}>
                  {s.props?.subheadline || "Your subheadline here"}
                </p>
                <div style={{ marginTop: 18 }}>
                  <WebsiteCta href={ctaHref} style={buttonStyle}>{s.props?.cta || "Get Started"}</WebsiteCta>
                </div>
              </div>
            );
          }

          if (s.type === "features") {
            const items = s.props?.items || [];
            return (
              <div key={idx} style={{ padding: "18px 0" }}>
                <h2 style={{ fontSize: 22, marginBottom: 12 }}>Why customers choose you</h2>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                  {items.map((it, j) => (
                    <div key={j} style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 18, padding: 14 }}>
                      <div style={{ fontWeight: 700 }}>{it.title}</div>
                      {!!it.desc && <div style={{ marginTop: 6, color: "rgba(255,255,255,0.70)", fontSize: 13 }}>{it.desc}</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (s.type === "trust") {
            const items = s.props?.items || [];
            if (!items.length) return null;
            return (
              <div key={idx} style={{ padding: "18px 0" }}>
                <h2 style={{ fontSize: 22, marginBottom: 12 }}>Store details</h2>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                  {items.map((it, j) => (
                    <div key={j} style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 18, padding: 14 }}>
                      <div style={{ fontWeight: 700 }}>{it.title}</div>
                      <div style={{ marginTop: 6, color: "rgba(255,255,255,0.70)", fontSize: 13 }}>{it.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (s.type === "testimonials") {
            const items = s.props?.items || [];
            if (!items.length) return null;
            return (
              <div key={idx} style={{ padding: "18px 0" }}>
                <h2 style={{ fontSize: 22, marginBottom: 12 }}>Testimonials</h2>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                  {items.map((it, j) => (
                    <div key={j} style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 18, padding: 14 }}>
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.80)" }}>"{it.quote || it.text || ""}"</div>
                      <div style={{ marginTop: 10, fontWeight: 700, fontSize: 13 }}>{it.name || "Customer"}</div>
                      {!!it.title && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.60)" }}>{it.title}</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (s.type === "faq") {
            const items = s.props?.items || [];
            if (!items.length) return null;
            return (
              <div key={idx} style={{ padding: "18px 0" }}>
                <h2 style={{ fontSize: 22, marginBottom: 12 }}>Questions before you order</h2>
                <div style={{ display: "grid", gap: 10 }}>
                  {items.map((it, j) => (
                    <div key={j} style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 14, padding: 14 }}>
                      <div style={{ fontWeight: 700 }}>{it.q || it.question}</div>
                      <div style={{ marginTop: 6, color: "rgba(255,255,255,0.70)", fontSize: 13 }}>{it.a || it.answer}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (s.type === "pricing") {
            const items = s.props?.items || [];
            return (
              <div key={idx} style={{ padding: "18px 0" }}>
                <h2 style={{ fontSize: 22, marginBottom: 12 }}>Pricing</h2>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                  {items.map((it, j) => (
                    <div key={j} style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 18, padding: 14 }}>
                      <div style={{ fontWeight: 700 }}>{it.name}</div>
                      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 8 }}>{it.price}</div>
                      {!!it.desc && <div style={{ marginTop: 6, color: "rgba(255,255,255,0.70)", fontSize: 13 }}>{it.desc}</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (s.type === "cta") {
            return (
              <div key={idx} style={{ padding: "28px 0" }}>
                <div style={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: 18, padding: 18, background: "rgba(255,255,255,0.05)" }}>
                  <div style={{ fontSize: 20, fontWeight: 800 }}>{s.props?.headline || "Ready to start?"}</div>
                  <div style={{ marginTop: 10 }}>
                    <WebsiteCta href={ctaHref} style={buttonStyle}>{s.props?.cta || "Get Started"}</WebsiteCta>
                  </div>
                </div>
              </div>
            );
          }

          return null;
        })}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.10)", padding: "18px 0", marginTop: 18, color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
          © 2026 — Website built by OmniMark HQ
        </div>
      </div>
    </div>
  );
}

export default function WebsiteBuilderPage() {
  const nav = useNavigate();
  const ws = getWorkspace();
  const qc = useQueryClient();

  const [businessName, setBusinessName] = useState("");
  const [offerPromise, setOfferPromise] = useState("");
  const [headline, setHeadline] = useState("");
  const [subheadline, setSubheadline] = useState("");
  const [cta, setCta] = useState("Get Started");
  const [ctaUrl, setCtaUrl] = useState("");
  const [features, setFeatures] = useState([]);
  const [accent, setAccent] = useState("#6C4AF2");
  const [siteType, setSiteType] = useState("service");
  const [productUrl, setProductUrl] = useState("");
  const [shippingNote, setShippingNote] = useState("");
  const [returnsNote, setReturnsNote] = useState("");

  const [projectId, setProjectId] = useState(null);
  const [schema, setSchema] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setBusinessName("");
    setOfferPromise("");
    setHeadline("");
    setSubheadline("");
    setCta("Get Started");
    setCtaUrl("");
    setFeatures([]);
    setSiteType("service");
    setProductUrl("");
    setShippingNote("");
    setReturnsNote("");
    setProjectId(null);
    setSchema(null);
    try {
      const handoff = getProductHandoff(ws?.id);
      if (!handoff?.product) return;
      setBusinessName(handoff.product);
      setOfferPromise(handoff.angle || `A better way to shop ${handoff.category || "this product"}.`);
      setHeadline(`${handoff.product}, made simple`);
      setSubheadline(handoff.angle || `Discover the benefits of ${handoff.product}.`);
      setCta("Shop Now");
      setSiteType("dropshipping");
      setProductUrl(handoff.source_url || "");
      setShippingNote(handoff.shipping_note || "");
      setReturnsNote(handoff.returns_note || "");
      setFeatures([
        handoff.angle || "Clear product benefit",
        handoff.theme ? `${handoff.theme} conversion layout` : "Conversion-ready layout",
        handoff.source_url ? "Source-linked product research" : "Customer-focused proof",
      ]);
    } catch {
      // Ignore malformed browser state and keep the builder usable.
    }
  }, [ws?.id]);

  const featureText = useMemo(() => features.join("\n"), [features]);

  async function generate() {
    if (!ws?.id) {
      setErr("Please select a workspace first.");
      return;
    }
    if (ctaUrl.trim() && !websiteCtaUrl(ctaUrl)) {
      setErr("Enter a valid button destination: an http(s), mailto or telephone URL.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const payload = {
        name: "Homepage",
        brand: { business_name: businessName, accent_color: accent },
        offer: {
          promise: offerPromise,
          headline,
          subheadline,
          cta,
          cta_url: ctaUrl.trim(),
          features: features.filter(Boolean),
          site_type: siteType,
          product_url: productUrl.trim(),
          shipping_note: shippingNote.trim(),
          returns_note: returnsNote.trim(),
        },
      };
      const res = await api.post(`/api/workspaces/${ws.id}/web-projects/generate`, payload);
      const pid = res.data.id;
      setProjectId(pid);

      // fetch project to get schema
      const pRes = await api.get(`/api/workspaces/${ws.id}/web-projects/${pid}`);
      setSchema(pRes.data.schema);

      // Invalidate web projects list
      qc.invalidateQueries({ queryKey: ["web_projects", ws.id] });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to generate website.");
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    if (!projectId || !ws?.id) return;
    if (!websiteCtaUrl(schema?.cta_url)) {
      setErr("Add a valid button destination and regenerate the preview before publishing.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const response = await api.post(`/api/workspaces/${ws.id}/web-projects/${projectId}/publish`);
      const publicPath = response.data?.public_path;
      if (response.data?.ok !== true || response.data?.project_id !== projectId || typeof publicPath !== "string" || !/^\/w\/[a-z0-9][a-z0-9-]{0,99}$/.test(publicPath)) {
        throw new Error("Publication could not be verified. No public link was opened; check the project status before retrying.");
      }

      // Invalidate web projects
      qc.invalidateQueries({ queryKey: ["web_projects", ws.id] });

      nav(publicPath);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to publish.");
    } finally {
      setBusy(false);
    }
  }

  if (!ws) {
    return (
      <div style={{ padding: 24, color: "#666" }}>
        Please select a workspace to use the Website Builder.
      </div>
    );
  }

  return (
    <div data-testid="website-builder-page" style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 16, padding: 16, height: "calc(100vh - 80px)" }}>
      <div style={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 14, padding: 14, background: "#fff", overflowY: "auto" }}>
        <div style={{ fontSize: 18, fontWeight: 800 }}>Website Designer</div>
        <div style={{ marginTop: 6, color: "#555", fontSize: 13 }}>
          Generate a high-converting landing page or a transparent, conversion-ready product storefront for this workspace.
        </div>

        {err && <div style={{ marginTop: 10, color: "#b00020", fontSize: 13 }}>{err}</div>}

        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 700 }}>
            Business Name
            <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} style={inputStyle} placeholder="Nichols Global Business Services" />
          </label>

          <label style={{ fontSize: 12, fontWeight: 700 }}>
            Offer Promise
            <input value={offerPromise} onChange={(e) => setOfferPromise(e.target.value)} style={inputStyle} placeholder="Describe the offer using facts you can support" />
          </label>

          <label style={{ fontSize: 12, fontWeight: 700 }}>
            Headline (optional)
            <input value={headline} onChange={(e) => setHeadline(e.target.value)} style={inputStyle} placeholder="We help you grow fast" />
          </label>

          <label style={{ fontSize: 12, fontWeight: 700 }}>
            Subheadline (optional)
            <input value={subheadline} onChange={(e) => setSubheadline(e.target.value)} style={inputStyle} placeholder="AI-built landing pages optimized for conversions" />
          </label>

          <label style={{ fontSize: 12, fontWeight: 700 }}>
            CTA Button
            <input value={cta} onChange={(e) => setCta(e.target.value)} style={inputStyle} />
          </label>

          <label style={{ fontSize: 12, fontWeight: 700 }}>
            Button destination URL
            <input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} style={inputStyle} placeholder="https://your-store.example/product or mailto:hello@example.com" />
          </label>
          <p style={{ fontSize: 12 }}>Use your intended customer destination, not a supplier research link. Generate a new preview after changing it. A destination is required before publishing.</p>

          <label style={{ fontSize: 12, fontWeight: 700 }}>
            Website type
            <select value={siteType} onChange={(e) => setSiteType(e.target.value)} style={inputStyle}>
              <option value="service">Service / lead-generation site</option>
              <option value="dropshipping">Dropshipping product page</option>
              <option value="storefront">Storefront collection page</option>
            </select>
          </label>

          {siteType !== "service" && (
            <>
              <label style={{ fontSize: 12, fontWeight: 700 }}>
                Product or supplier source URL
                <input value={productUrl} onChange={(e) => setProductUrl(e.target.value)} style={inputStyle} placeholder="Product URL from the Dropshipping Lab or connected store" />
              </label>
              <label style={{ fontSize: 12, fontWeight: 700 }}>
                Accurate shipping note
                <input value={shippingNote} onChange={(e) => setShippingNote(e.target.value)} style={inputStyle} placeholder="Example: Processing 1–2 business days; delivery 5–8 business days" />
              </label>
              <label style={{ fontSize: 12, fontWeight: 700 }}>
                Accurate returns note
                <input value={returnsNote} onChange={(e) => setReturnsNote(e.target.value)} style={inputStyle} placeholder="Example: 30-day returns on unused items; see full policy" />
              </label>
              <div style={{ fontSize: 12, lineHeight: 1.45, color: "#555", border: "1px solid #f1d08a", borderRadius: 10, padding: 10, background: "#fff8e8" }}>
                OmniMark will not invent delivery times, return terms, reviews, or product claims. Add verified terms before publishing.
              </div>
            </>
          )}

          <label style={{ fontSize: 12, fontWeight: 700 }}>
            Features (one per line)
            <textarea
              value={featureText}
              onChange={(e) => setFeatures(e.target.value.split("\n").map((x) => x.trim()))}
              style={{ ...inputStyle, height: 110, resize: "vertical" }}
            />
          </label>

          <label style={{ fontSize: 12, fontWeight: 700 }}>
            Accent Color
            <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} style={{ width: 64, height: 38, border: "none", background: "transparent" }} />
          </label>

          <button onClick={generate} disabled={busy} style={primaryBtn}>
            {busy ? "Working…" : "Generate Website"}
          </button>

          <button onClick={publish} disabled={busy || !projectId || !websiteCtaUrl(schema?.cta_url)} style={secondaryBtn}>
            Publish to Public Page
          </button>

          <div style={{ fontSize: 12, color: "#666" }}>
            Public URL: <code>/w/{ws.slug || ":slug"}</code>
          </div>
        </div>
      </div>

      <div style={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 14, overflow: "hidden", overflowY: "auto" }}>
        {!schema ? (
          <div style={{ padding: 18, color: "#666", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🌐</div>
              <div>Preview will appear here after you generate.</div>
            </div>
          </div>
        ) : (
          <SectionRenderer schema={schema} />
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  borderRadius: 12,
  border: "1px solid rgba(0,0,0,0.12)",
  padding: "10px 12px",
  marginTop: 6,
  outline: "none",
  fontFamily: "inherit",
  fontSize: 14,
};

const primaryBtn = {
  borderRadius: 12,
  border: "none",
  padding: "12px 14px",
  fontWeight: 900,
  background: "#111",
  color: "#fff",
  cursor: "pointer",
  fontSize: 14,
};

const secondaryBtn = {
  borderRadius: 12,
  border: "1px solid rgba(0,0,0,0.12)",
  padding: "12px 14px",
  fontWeight: 900,
  background: "transparent",
  color: "#111",
  cursor: "pointer",
  fontSize: 14,
};
